import { useEffect, useMemo, useRef, useState } from "react";
import { ADDITIVES, ALLERGENS, CARD_LANGS, CARD_LANG_LABELS, PHRASES_ID, PHRASE_ORIGINS, cardLangsOf, cardPhrases, cardPhrasesOf, frozenTexts, phraseChange, phraseKey, phraseOf, webViewOf, cardWebHtml, editsMasterData, imprintLink, webCardOf, webCardSettingsOf, CARD_COLOR, CARD_COLORS, CELLAR_PROFILES, DEFAULT_RUBRICS, PART_TITLES, PROFILES, PROFILE_TITLES, RECIPE_KINDS, WINE_COLORS, WINE_MEASURES, allergenRows, cheapestRule, formatHeadings, parseMeasure, parseHeadings, partOrder, profileFor, uuidv7, cardDiff, cardDigest, drinksCard, formatCustomMeasures, formatGlasses, isBulk, isProfiled, isUpToDate, parseCustomMeasures, parseGlasses, stockLines } from "@lagerlog/core";
import type { CardLang, CardPhrases, PhraseOrigin, CardDiff, CardOptions, CardPart, Component, CustomMeasure, ExistingArticle, GlassPrice, PrintedLine, Recipe, Rubric, State } from "@lagerlog/core";
import type { Article, Catalog } from "../catalog.ts";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import { useWide } from "../wide.ts";
import { CARD_VARIANTS, allergenHtml, cardHtml, cardName } from "../cardHtml.ts";
import type { CardVariant } from "../cardHtml.ts";
import { fmt } from "./Book.tsx";
import { containerWords, decimalGlasses, drinkProfile, euro, usedProfiles } from "../catalog.ts";
import type { WordStyles } from "../cardDocx.ts";
import { printPage as print } from "../print.ts";
import { qrPng, qrSvg, stickerSheetHtml } from "../qr.ts";

const VARIANT_KEY = "lagerlog:card-variant";

/** What the field of glass prices proposes, grey in the empty field, per profile. */
const GLASS_SUGGESTION: Record<string, string> = { bier: "0,3 l 3,80; 0,5 l 5,20", alkoholfrei: "0,2 l 2,80; 0,4 l 4,90", spirituosen: "2 cl 4,50; 4 cl 8,00", weitere: "0,2 l 4,50" };

function readVariant(): CardVariant {
  try {
    return CARD_VARIANTS.find((v) => v.key === localStorage.getItem(VARIANT_KEY)) ?? CARD_VARIANTS[0]!;
  } catch {
    return CARD_VARIANTS[0]!;
  }
}

/** Hands a file to the user: the browser saves it (or offers to open it in Word on an iPad). */
function save(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The uploaded Word styles of this workspace (entity card/word), or null: the app's own. */
/** The workspace's own names of measures („Schoppen = 0,5 l“), kept as master data `card` with id `measures`. */
export function measuresOf(catalog: Catalog): CustomMeasure[] {
  const names = catalog.cards.get("measures")?.names;
  return Array.isArray(names) ? (names as CustomMeasure[]).filter((n) => typeof n?.name === "string" && typeof n?.ml === "number") : [];
}

/** Own names for glasses, the last level of the measures (docs/17-getraenke.md, Maße mit Namen). */
function MeasuresBox({ measures, decimal }: { measures: CustomMeasure[]; decimal: boolean }) {
  const { store } = useStore();
  const saved = formatCustomMeasures(measures);
  const [text, setText] = useState(saved);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    if (text.trim() === saved) return;
    const parsed = parseCustomMeasures(text);
    if ("error" in parsed) return setError(parsed.error);
    setError(null);
    setText(formatCustomMeasures(parsed.value));
    await store.edit("card", "measures", { names: parsed.value });
  }
  return (
    <details className="word-styles" data-testid="measures">
      <summary>Eigene Glasgrößen</summary>
      <p className="muted">
        Regionale Namen für Gläser, eine je Zeile, etwa „Schoppen = 0,5 l“ oder „Viertele = 0,25 l“. Sie gelten beim Eingeben der Glaspreise und stehen auf der Karte mit der Menge: „Schoppen 0,5 l“.
      </p>
      <textarea rows={3} value={text} placeholder="Schoppen = 0,5 l" onChange={(e) => setText(e.target.value)} onBlur={save} data-testid="measures-text" />
      {error && <p className="error">{error}</p>}
      <label className="check">
        <input type="checkbox" checked={decimal} onChange={(e) => void store.edit("card", "measures", { decimal: e.target.checked })} data-testid="measures-decimal" />
        Glasgrößen als Dezimalzahl: „0,25 l“ statt „1/4 l“
      </label>
    </details>
  );
}

export function wordStylesOf(catalog: Catalog): WordStyles | null {
  const f = catalog.cards.get("word");
  return f && typeof f.styles === "string" ? (f as unknown as WordStyles) : null;
}

const money = (cents: unknown) => (typeof cents === "number" ? (cents / 100).toFixed(2).replace(".", ",") : "");
const glasses = (a: Article) => (Array.isArray(a.fields.byGlass) ? (a.fields.byGlass as GlassPrice[]) : []);
const listNo = (a: Article) => (typeof a.fields.listNo === "string" ? a.fields.listNo : "");
const byNo = (a: Article, b: Article) => (parseInt(listNo(a), 10) || 1e9) - (parseInt(listNo(b), 10) || 1e9) || a.name.localeCompare(b.name, "de");

/** Bottles per wine over all storages (stock is in ml): the card is the whole house's, not this device's. */
export function stockOf(ledger: State, catalog: Catalog): Map<string, number> {
  const out = new Map<string, number>();
  for (const l of stockLines(ledger)) out.set(l.article, (out.get(l.article) ?? 0) + l.qty / (catalog.articles.get(l.article)?.bottle ?? 1));
  return out;
}

/** The wines the owner decides about: in stock, or priced for the card. A private cellar: in stock. */
export function cardWines(catalog: Catalog, stock: Map<string, number>, own: boolean): Article[] {
  // The card for guests has every profile (block 3); the cellar list the profiles switched on.
  const on = own ? cellarProfiles(catalog) : PROFILES.map((p) => p.id);
  return [...catalog.articles.values()].filter((a) => {
    const p = drinkProfile(a);
    return p !== null && on.includes(p) && (p !== "wein" || isProfiled(a.fields)) && ((stock.get(a.id) ?? 0) > 0 || (!own && typeof a.fields.salePrice === "number"));
  });
}

/** How the owner set up the card (master data `card` „menu“): rubrics, order, own headings, where the allergens go. */
export function menuOf(catalog: Catalog): { rubrics: Rubric[]; order: string[] | undefined; headings: Record<string, string>; allergens: "card" | "list" } {
  const f = catalog.cards.get("menu") ?? {};
  const rubrics = Array.isArray(f.rubrics) ? (f.rubrics as Rubric[]).filter((r) => typeof r?.id === "string" && typeof r?.title === "string") : [...DEFAULT_RUBRICS];
  return {
    rubrics,
    order: Array.isArray(f.order) ? (f.order as string[]) : undefined,
    headings: f.headings && typeof f.headings === "object" ? (f.headings as Record<string, string>) : {},
    allergens: f.allergens === "list" ? "list" : "card",
  };
}

/** The card's options from the catalogue, the same for the view, the print and the check whether the printed one is outdated. */
export function cardOptions(catalog: Catalog, own: boolean): Pick<CardOptions, "measures" | "decimal" | "profiles" | "recipes" | "rubrics" | "order" | "headings"> {
  const menu = menuOf(catalog);
  return { measures: measuresOf(catalog), decimal: decimalGlasses(catalog), profiles: cellarProfiles(catalog), ...(own ? {} : { recipes: [...catalog.recipes.values()], rubrics: menu.rubrics, order: menu.order, headings: menu.headings }) };
}

/** The profiles on the cellar list: as the cellar chose (master data `card` „cellar“), else wine, spirits and further drinks. */
export function cellarProfiles(catalog: Catalog): string[] {
  const chosen = catalog.cards.get("cellar")?.profiles;
  return Array.isArray(chosen) ? chosen.filter((p): p is string => typeof p === "string") : [...CELLAR_PROFILES];
}

/** The printed card (or Kellerliste) against the card as it would be now; null: never printed. */
export function printStatus(catalog: Catalog, parts: CardPart[], own: boolean): { printedAt: number; diff: CardDiff } | null {
  const printed = catalog.cards.get(own ? "cellar" : "guests");
  if (!printed || typeof printed.printedAt !== "number" || !Array.isArray(printed.lines)) return null;
  return { printedAt: printed.printedAt, diff: cardDiff(printed.lines as PrintedLine[], cardDigest(parts)) };
}

/**
 * The card on the net (F22) against the card as it would be now; null: never published. With
 * „Ausgetrunkenes ausblenden“ a drink drunk up is not counted as gone: the page leaves it out by itself.
 */
export function webStatus(catalog: Catalog, parts: CardPart[], stock: Map<string, number>): { printedAt: number; diff: CardDiff } | null {
  const f = catalog.cards.get("web");
  const card = webCardOf(f);
  if (!card || !Array.isArray(f?.lines)) return null;
  const diff = cardDiff(f.lines as PrintedLine[], cardDigest(parts));
  const drunk = (l: PrintedLine) => catalog.articles.has(l.key.split("|")[0]!) && (stock.get(l.key.split("|")[0]!) ?? 0) <= 0;
  return { printedAt: card.publishedAt, diff: webCardSettingsOf(f).hideSoldOut ? { ...diff, gone: diff.gone.filter((l) => !drunk(l)) } : diff };
}

/** Whether the printed card is out of date, for the link under Weine. */
export function cardOutdated(catalog: Catalog, ledger: State, own: boolean): boolean {
  const stock = stockOf(ledger, catalog);
  const existing = cardWines(catalog, stock, own).map((a) => ({ id: a.id, fields: a.fields, stock: stock.get(a.id) ?? 0 }));
  const status = printStatus(catalog, drinksCard(existing, { grouping: "number", private: own, ...cardOptions(catalog, own) }), own);
  return status !== null && !isUpToDate(status.diff);
}

/**
 * The wine list for guests (K11, docs/15-getraenkekarte.md): which wines are on it, at which price,
 * which also by the glass; then print. Every change is an edit of the wine and syncs like any other.
 * A private cellar gets the Kellerliste instead: every wine in stock with bottles and drinking
 * window, no prices; per wine only „auf der Liste“ and the number one may stick on the shelf.
 */
export function CardView({ onDone }: { onDone: () => void }) {
  const { store, version } = useStore();
  const catalog = useCatalog();
  const drinks = useDrinks();
  const ledger = useLedger();
  const wide = useWide();
  const [variant, setVariant] = useState(readVariant);
  const own = store.config?.kind === "private";

  const stock = useMemo(
    () => (ledger && catalog ? stockOf(ledger, catalog) : new Map<string, number>()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ledger, catalog, version],
  );

  if (!catalog || !ledger) return null;

  const wines = cardWines(catalog, stock, own);
  const existing: ExistingArticle[] = wines.map((a) => ({ id: a.id, fields: a.fields, stock: stock.get(a.id) ?? 0 }));
  const measures = measuresOf(catalog);
  const onList = cellarProfiles(catalog);
  const menu = menuOf(catalog);
  const parts = drinksCard(existing, { grouping: variant.grouping, private: own, ...cardOptions(catalog, own) });
  const count = (title: string) => parts.find((p) => p.title === title)?.sections.reduce((n, s) => n + s.groups.reduce((m, g) => m + g.entries.reduce((k, e) => k + e.lines.length, 0), 0), 0) ?? 0;
  const lines = parts.flatMap((p) => p.sections.flatMap((s) => s.groups.flatMap((g) => g.entries.flatMap((e) => e.lines))));
  const html = cardHtml(parts, variant, { house: store.config?.workspace ?? "Weinkarte", date: new Date(), private: own, allergens: menu.allergens });
  const rule = own ? null : cheapestRule(parts);
  const hidden = wines.filter((a) => a.fields.offCard === true).length;
  const status = printStatus(catalog, parts, own);
  const printNow = async () => {
    print(html);
    await store.edit("card", own ? "cellar" : "guests", { printedAt: Date.now(), variant: variant.key, lines: cardDigest(parts) });
  };
  const house = store.config?.workspace ?? "Weinkarte";
  const word = async () => {
    const { cardDocx } = await import("../cardDocx.ts");
    save(await cardDocx(parts, variant, { house, date: new Date(), private: own, styles: wordStylesOf(catalog), allergens: menu.allergens }), `${cardName(parts, own)} ${house} ${variant.key.toUpperCase()}.docx`);
  };
  // The card on the net (F22): layout D, grouped like A; only for those who keep up the card.
  const keeper = !own && editsMasterData(store.rights().role);
  const webParts = keeper ? drinksCard(existing, { grouping: "colorOrigin", ...cardOptions(catalog, false) }) : [];
  const web = keeper ? webStatus(catalog, webParts, stock) : null;
  // The languages switched on, each with the house's own translations of the card's texts (F28).
  const texts = keeper ? frozenTexts(webParts, cardLangsOf(catalog.cards.get("web")?.languages), cardPhrasesOf(catalog.cards.get(PHRASES_ID))) : {};
  const textsChanged = web !== null && sameTexts(webCardOf(catalog.cards.get("web"))?.texts ?? {}, texts) === false;
  const publish = () => store.edit("card", "web", { publishedAt: Date.now(), house, parts: webParts, allergens: menu.allergens, lines: cardDigest(webParts), texts });
  // Without a price is only what has neither a price for the bottle nor one by the glass (B4).
  const withoutPrice = own ? 0 : wines.filter((a) => a.fields.offCard !== true && typeof a.fields.salePrice !== "number" && !(Array.isArray(a.fields.byGlass) && a.fields.byGlass.length > 0)).length;

  const choose = (v: CardVariant) => {
    setVariant(v);
    try {
      localStorage.setItem(VARIANT_KEY, v.key);
    } catch {
      // Only a convenience.
    }
  };

  // Sections of the list in the view: the wines by colour, then each other profile on the list by kind.
  const groups: { key: string; title: string; items: Article[] }[] = [];
  for (const p of PROFILES) {
    if (own && !onList.includes(p.id)) continue;
    const kinds = p.id === "wein" ? [...WINE_COLORS].sort((a, b) => CARD_COLORS.indexOf(a) - CARD_COLORS.indexOf(b)) : (p.fields.find((f) => f.key === p.categoryField)?.options ?? []);
    for (const k of kinds) {
      const items = wines.filter((a) => drinkProfile(a) === p.id && a.fields.color === k);
      if (items.length) groups.push({ key: `${p.id}|${k}`, title: p.id === "wein" ? (k === "Schaumwein" ? "Sekt" : `${CARD_COLOR[k] ?? k}e`) : k, items });
    }
  }
  const used = usedProfiles(catalog);

  const list = (
    <div className="card-admin">
      <p className="row between">
        <button className="link" onClick={onDone}>
          ← {drinks ? "Getränke" : "Weine"}
        </button>
        <span className="row card-actions">
          <button onClick={word} data-testid="card-word">
            Word-Datei
          </button>
          {keeper && (
            <button onClick={publish} data-testid="card-publish">
              Veröffentlichen
            </button>
          )}
          <button className="primary" onClick={printNow} data-testid="card-print">
            Drucken
          </button>
        </span>
      </p>
      <h2>{own ? "Kellerliste" : "Getränkekarte"}</h2>
      <p className="muted" data-testid="card-summary">
        {own ? `${parts.reduce((n, p) => n + count(p.title), 0)} ${used.length > 1 ? "Getränke" : "Weine"} auf der Liste` : `${lines.filter((l) => l.ml === undefined).length} Flaschen und ${lines.filter((l) => l.ml !== undefined).length} Gläser auf der Karte`}
        {hidden > 0 && ` · ${hidden} ausgeblendet`}
        {withoutPrice > 0 && ` · ${withoutPrice} ohne Preis`}
      </p>
      <PrintNote status={status} own={own} />
      {web && <PrintNote status={web} own={false} web />}
      <div className="variants">
        {CARD_VARIANTS.map((v) => (
          <button key={v.key} className={v.key === variant.key ? "active" : ""} onClick={() => choose(v)} data-testid={`card-variant-${v.key}`}>
            {v.title}
          </button>
        ))}
      </div>
      {rule && (
        <p className="banner-box" data-testid="price-rule">
          Hinweis: Das billigste alkoholfreie Getränk ({rule.free.name}, {euro(rule.free.perLitre)} je Liter) kostet mehr als das billigste alkoholische ({rule.alcoholic.name}, {euro(rule.alcoholic.perLitre)} je Liter). Nach dem Gaststättenrecht muss mindestens ein alkoholfreies Getränk nicht teurer sein.
        </p>
      )}
      {keeper && <WebCardBox catalog={catalog} parts={webParts} house={house} allergens={menu.allergens} published={web !== null} texts={texts} textsChanged={textsChanged} onPublish={publish} />}
      <WordStylesBox own={wordStylesOf(catalog)} />
      {!own && <MeasuresBox measures={measures} decimal={decimalGlasses(catalog)} />}
      {!own && <MenuBox catalog={catalog} parts={parts} />}
      {!own && <RecipesBox catalog={catalog} stock={stock} rubrics={menu.rubrics} measures={measures} />}
      {own && used.length > 1 && (
        <div className="list-profiles" data-testid="cellar-profiles">
          <span className="muted">Auf der Liste:</span>
          {used.map((id) => (
            <label key={id} className="check">
              <input
                type="checkbox"
                checked={onList.includes(id)}
                onChange={(e) => store.edit("card", "cellar", { profiles: PROFILES.map((p) => p.id).filter((p) => (p === id ? e.target.checked : onList.includes(p))) })}
                data-testid={`cellar-profile-${id}`}
              />
              {PROFILE_TITLES[id]}
            </label>
          ))}
        </div>
      )}
      {groups.map((g) => (
        <section key={g.key}>
          <h3>{g.title}</h3>
          <ul className="list">
            {g.items
              .sort(byNo)
              .map((a) => (
                <CardRow key={a.id} a={a} stock={stock.get(a.id) ?? 0} own={own} measures={measures} decimal={decimalGlasses(catalog)} rubrics={menu.rubrics} />
              ))}
          </ul>
        </section>
      ))}
      {wines.length === 0 && <p>{own ? `Noch keine ${drinks ? "Getränke" : "Weine"} im Keller.` : `Noch keine ${drinks ? "Getränke" : "Weine"} mit Bestand oder Preis.`}</p>}
    </div>
  );

  if (!wide) return list;
  return (
    <div className="split">
      <div className="pane">{list}</div>
      <div className="pane side-pane">
        <iframe className="card-preview" title={own ? "Vorschau der Kellerliste" : "Vorschau der Getränkekarte"} srcDoc={html} data-testid="card-preview" />
      </div>
    </div>
  );
}

/** One wine: on the card or not, its bottle price, and by the glass with the glass prices. */
function CardRow({ a, stock, own, measures, decimal, rubrics }: { a: Article; stock: number; own: boolean; measures: CustomMeasure[]; decimal: boolean; rubrics: Rubric[] }) {
  if (own) return <ListRow a={a} stock={stock} />;
  return <GuestRow a={a} stock={stock} measures={measures} decimal={decimal} rubrics={rubrics} />;
}

/** A wine of a private cellar: on the list or not, and its number (e.g. on the shelf). */
function ListRow({ a, stock }: { a: Article; stock: number }) {
  const { store } = useStore();
  const [no, setNo] = useState(listNo(a));
  const onList = a.fields.offCard !== true;
  const save = (fields: Record<string, unknown>) => store.edit("article", a.id, fields);
  const saveNo = () => {
    const raw = no.trim();
    if (raw !== listNo(a)) void save({ listNo: raw === "" ? null : raw });
  };
  const from = a.fields.drinkFrom;
  const to = a.fields.drinkTo;
  return (
    <li className={`card-row ${onList ? "" : "off"}`} data-testid={`card-row-${listNo(a) || a.id}`}>
      <div className="card-name">
        <span className="no">{listNo(a)}</span>
        <span>
          {a.name}
          <br />
          <span className="muted">
            {fmt(a, stock * a.bottle)}
            {from || to ? ` · trinkreif ${from ?? "…"}–${to ?? "…"}` : ""}
          </span>
        </span>
      </div>
      <label className="card-price">
        Nummer
        <input value={no} placeholder="–" onChange={(e) => setNo(e.target.value)} onBlur={saveNo} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} data-testid="card-number" />
      </label>
      <div className="card-switches">
        <label className="check">
          <input type="checkbox" checked={onList} onChange={(e) => save({ offCard: e.target.checked ? null : true })} data-testid="card-on" />
          auf der Liste
        </label>
      </div>
    </li>
  );
}

/** A wine of a business: on the card or not, its bottle price, and by the glass with the glass prices. */
function GuestRow({ a, stock, measures, decimal, rubrics }: { a: Article; stock: number; measures: CustomMeasure[]; decimal: boolean; rubrics: Rubric[] }) {
  const { store } = useStore();
  const profile = profileFor(a.fields);
  const ctx = { profile: profile.measures ?? WINE_MEASURES, ...(typeof a.fields.color === "string" ? { kind: a.fields.color } : {}), custom: measures, decimal };
  const saved = { price: money(a.fields.salePrice), glass: formatGlasses(glasses(a), ctx) };
  const [price, setPrice] = useState(saved.price);
  const [glass, setGlass] = useState(saved.glass);
  // The field follows the saved prices as written now (decimals switched, B5), unless it is being edited.
  const shownGlass = useRef(saved.glass);
  useEffect(() => {
    if (glass === shownGlass.current) setGlass(saved.glass);
    shownGlass.current = saved.glass;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved.glass]);
  const [open, setOpen] = useState(glasses(a).length > 0);
  const [error, setError] = useState<string | null>(null);
  const onCard = a.fields.offCard !== true;
  // A keg is on the card only by the glass: no price for the whole (drinks-card.ts).
  const keg = isBulk(a.fields);
  const save = (fields: Record<string, unknown>) => store.edit("article", a.id, fields);

  async function savePrice() {
    const raw = price.trim();
    if (raw === saved.price) return;
    if (raw === "") return save({ salePrice: null }).then(() => setError(null));
    const n = Number(raw.replace(/\s|€/g, "").replace(",", "."));
    if (!Number.isFinite(n) || n < 0) return setError("Preis: bitte einen Betrag wie 39,50");
    setError(null);
    setPrice(money(Math.round(n * 100)));
    await save({ salePrice: Math.round(n * 100) });
  }

  async function saveGlass() {
    if (glass.trim() === saved.glass) return;
    const parsed = parseGlasses(glass, ctx);
    if ("error" in parsed) return setError(`Glas: ${parsed.error}`);
    setError(null);
    setGlass(formatGlasses(parsed.value, ctx));
    await save({ byGlass: parsed.value.length ? parsed.value : null });
  }

  async function toggleOpen(next: boolean) {
    setOpen(next);
    if (!next && glasses(a).length) {
      setGlass("");
      await save({ byGlass: null });
    }
  }

  const suggestion =
    profile.id !== "wein" ? (GLASS_SUGGESTION[profile.id] ?? "0,2 l 3,50") : a.fields.color === "Schaumwein" ? "0,1 l 7,50" : a.fields.color === "Likörwein" ? "5 cl 6,50" : "1/8 l 6,50; 1/4 l 12,50";
  return (
    <li className={`card-row ${onCard ? "" : "off"}`} data-testid={`card-row-${listNo(a) || a.id}`}>
      <div className="card-name">
        <span className="no">{listNo(a)}</span>
        <span>
          {a.name}
          <br />
          <span className="muted">{stock > 0 ? fmt(a, stock * a.bottle) : "kein Bestand"}</span>
        </span>
      </div>
      {keg ? (
        <span className="card-price muted" data-testid="card-price-keg">
          nur offen
        </span>
      ) : (
        <label className="card-price">
          {containerWords(a).one} €
          <input inputMode="decimal" value={price} placeholder="–" onChange={(e) => setPrice(e.target.value)} onBlur={savePrice} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} data-testid="card-price" />
        </label>
      )}
      <div className="card-switches">
        <label className="check">
          <input type="checkbox" checked={onCard} onChange={(e) => save({ offCard: e.target.checked ? null : true })} data-testid="card-on" />
          auf der Karte
        </label>
        <label className="check">
          <input type="checkbox" checked={open} disabled={!onCard} onChange={(e) => toggleOpen(e.target.checked)} data-testid="card-open" />
          auch offen
        </label>
      </div>
      {open && onCard && (
        <label className="card-glass">
          Glaspreise
          <input value={glass} placeholder={suggestion} data-suggest title="Tab übernimmt den Vorschlag" onChange={(e) => setGlass(e.target.value)} onBlur={saveGlass} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} data-testid="card-glass" />
        </label>
      )}
      {onCard && rubrics.length > 0 && (
        <label className="card-rubric">
          Rubrik
          <select value={typeof a.fields.rubric === "string" ? a.fields.rubric : ""} onChange={(e) => save({ rubric: e.target.value || null })} data-testid="card-rubric">
            <option value="">– (im Teil {profile.id === "wein" ? "der Weine" : PROFILE_TITLES[profile.id]})</option>
            {rubrics.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title}
              </option>
            ))}
          </select>
        </label>
      )}
      {onCard && (keg || price.trim() === "") && !error && !open && <p className="hint">Ohne {keg ? "Glaspreise steht das Fass" : profile.id === "wein" ? "Flaschenpreis steht der Wein" : "Preis oder Glaspreise steht es"} nicht auf der Karte.</p>}
      {error && <p className="error">{error}</p>}
    </li>
  );
}

/** The title of a part, for the order: a rubric's own, else the profile's. */
const partTitle = (key: string, rubrics: Rubric[]) => (key.startsWith("rubric:") ? (rubrics.find((r) => `rubric:${r.id}` === key)?.title ?? key) : (PART_TITLES[key] ?? key));

/**
 * How the card is built (docs/17-getraenke.md, Die Getränkekarte): the order of its parts, the
 * rubrics, own headings, and where the allergens go. Kept as master data `card` „menu“.
 */
function MenuBox({ catalog, parts }: { catalog: Catalog; parts: CardPart[] }) {
  const { store } = useStore();
  const menu = menuOf(catalog);
  const order = partOrder(menu.order, menu.rubrics);
  const [headings, setHeadings] = useState(formatHeadings(menu.headings));
  const [rubric, setRubric] = useState("");
  const edit = (fields: Record<string, unknown>) => store.edit("card", "menu", fields);
  const move = (i: number, by: number) => {
    const next = [...order];
    const [k] = next.splice(i, 1);
    next.splice(i + by, 0, k!);
    void edit({ order: next });
  };
  const shown = new Set(parts.map((p) => p.key));
  return (
    <details className="word-styles menu-box" data-testid="menu">
      <summary>Aufbau der Karte</summary>
      <h4>Reihenfolge</h4>
      <p className="muted">Teile ohne Getränke erscheinen nicht (grau).</p>
      <ol className="order">
        {order.map((k, i) => (
          <li key={k} className={shown.has(k) ? "" : "muted"}>
            <span>{partTitle(k, menu.rubrics)}</span>
            <span className="row">
              <button className="link" disabled={i === 0} onClick={() => move(i, -1)} aria-label="nach oben" data-testid={`order-up-${k}`}>
                ↑
              </button>
              <button className="link" disabled={i === order.length - 1} onClick={() => move(i, 1)} aria-label="nach unten">
                ↓
              </button>
            </span>
          </li>
        ))}
      </ol>
      <h4>Rubriken</h4>
      <p className="muted">Eigene Teile über die Profile hinweg; ein Getränk kommt mit „Rubrik“ in seiner Zeile hinein, ein zubereitetes nach seiner Art.</p>
      <ul className="rubrics">
        {menu.rubrics.map((r) => (
          <li key={r.id} className="row">
            <input defaultValue={r.title} onBlur={(e) => e.target.value.trim() && e.target.value !== r.title && edit({ rubrics: menu.rubrics.map((x) => (x.id === r.id ? { ...x, title: e.target.value.trim() } : x)) })} />
            <button className="link" onClick={() => edit({ rubrics: menu.rubrics.filter((x) => x.id !== r.id) })}>
              entfernen
            </button>
          </li>
        ))}
      </ul>
      <p className="row">
        <input value={rubric} placeholder="Neue Rubrik, z. B. Empfehlung des Hauses" onChange={(e) => setRubric(e.target.value)} data-testid="rubric-new" />
        <button disabled={!rubric.trim()} onClick={() => [void edit({ rubrics: [...menu.rubrics, { id: uuidv7(Date.now()), title: rubric.trim() }] }), setRubric("")]}>
          Anlegen
        </button>
      </p>
      <h4>Überschriften</h4>
      <p className="muted">Eigene Namen, eine je Zeile: „Sekt = Sekt &amp; Champagner“, „Biere = Biere vom Fass“. Im Katalog bleibt es, wie es ist.</p>
      <textarea rows={3} value={headings} placeholder="Sekt = Sekt & Champagner" onChange={(e) => setHeadings(e.target.value)} onBlur={() => edit({ headings: parseHeadings(headings) })} data-testid="headings" />
      <h4>Allergene</h4>
      <label className="check">
        <input type="radio" name="allergens" checked={menu.allergens === "card"} onChange={() => edit({ allergens: "card" })} />
        auf der Karte, bei jedem Getränk
      </label>
      <label className="check">
        <input type="radio" name="allergens" checked={menu.allergens === "list"} onChange={() => edit({ allergens: "list" })} data-testid="allergens-list" />
        nur in einer Liste für das Personal
      </label>
      <p>
        <button onClick={() => print(allergenHtml(allergenRows(parts), store.config?.workspace ?? "", new Date()))}>Allergenliste drucken</button>
      </p>
      <p className="hint">Zusatzstoffe bekommen eine Fußnote mit fester Nummer (1 mit Farbstoff … 9 chininhaltig); die Erklärung steht unten auf der Karte. Bitte vor dem ersten Druck rechtlich prüfen.</p>
    </details>
  );
}

/** Prepared drinks: on the card without stock of their own (docs/17-getraenke.md, Zubereitete Getränke). */
function RecipesBox({ catalog, stock, rubrics, measures }: { catalog: Catalog; stock: Map<string, number>; rubrics: Rubric[]; measures: CustomMeasure[] }) {
  const [editing, setEditing] = useState<string | null>(null);
  const recipes = [...catalog.recipes.values()].sort((a, b) => a.name.localeCompare(b.name, "de"));
  return (
    <section className="recipes" data-testid="recipes">
      <h3>Zubereitete Getränke</h3>
      <p className="muted">Spritz, Gin Tonic, Schorle: auf der Karte, aber ohne eigenen Bestand. Bestandteile sind freiwillig; gebucht wird noch nichts.</p>
      <ul className="list">
        {recipes.map((r) =>
          editing === r.id ? (
            <RecipeForm key={r.id} recipe={r} catalog={catalog} stock={stock} rubrics={rubrics} measures={measures} onDone={() => setEditing(null)} />
          ) : (
            <li key={r.id} className={`row between ${r.offCard ? "off" : ""}`}>
              <button className="link" onClick={() => setEditing(r.id)} data-testid={`recipe-${r.name}`}>
                {r.name}
              </button>
              <span className="side muted">{formatGlasses(r.glasses, { custom: measures }) || "kein Preis"}</span>
            </li>
          ),
        )}
      </ul>
      {editing === "new" ? (
        <RecipeForm catalog={catalog} stock={stock} rubrics={rubrics} measures={measures} onDone={() => setEditing(null)} />
      ) : (
        <button onClick={() => setEditing("new")} data-testid="recipe-new">
          + Zubereitetes Getränk
        </button>
      )}
    </section>
  );
}

function RecipeForm({ recipe, catalog, stock, rubrics, measures, onDone }: { recipe?: Recipe; catalog: Catalog; stock: Map<string, number>; rubrics: Rubric[]; measures: CustomMeasure[]; onDone: () => void }) {
  const { store } = useStore();
  const [name, setName] = useState(recipe?.name ?? "");
  const [kind, setKind] = useState(recipe?.kind === "Schorle" ? "Saftschorle" : (recipe?.kind ?? "Longdrink"));
  const [glasses, setGlasses] = useState(recipe ? formatGlasses(recipe.glasses, { custom: measures }) : "");
  const [text, setText] = useState(recipe?.cardText ?? "");
  const [listNo, setListNo] = useState(recipe?.listNo ?? "");
  const [rubric, setRubric] = useState(recipe?.rubric ?? "");
  const [additives, setAdditives] = useState<string[]>(recipe?.additives ?? []);
  const [allergens, setAllergens] = useState<string[]>(recipe?.allergens ?? []);
  const [components, setComponents] = useState<{ article: string; amount: string }[]>((recipe?.components ?? []).map((c) => ({ article: c.article, amount: formatGlasses([{ ml: c.ml, price: 0 }]).replace(/ 0,00$/, "") })));
  const [offCard, setOffCard] = useState(recipe?.offCard ?? false);
  const [error, setError] = useState<string | null>(null);
  const drinks = [...catalog.articles.values()].filter((a) => drinkProfile(a)).sort((a, b) => a.name.localeCompare(b.name, "de"));
  // What the parts bring along, proposed: a Gin Tonic is chininhaltig by its tonic.
  const from = (key: "additives" | "allergens") => [...new Set(components.flatMap((c) => (catalog.articles.get(c.article)?.fields[key] as string[] | undefined) ?? []))];
  const proposed = { additives: from("additives").filter((a) => !additives.includes(a)), allergens: from("allergens").filter((a) => !allergens.includes(a)) };
  const missing = components.filter((c) => c.article && (stock.get(c.article) ?? 0) <= 0).map((c) => catalog.articles.get(c.article)?.name ?? c.article);
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  async function save() {
    if (!name.trim()) return setError("Bitte einen Namen");
    const parsed = parseGlasses(glasses, { custom: measures });
    if ("error" in parsed) return setError(`Preise: ${parsed.error}`);
    const parts: Component[] = [];
    for (const c of components) {
      if (!c.article) continue;
      const ml = parseMeasure(c.amount, { custom: measures });
      if (ml === null) return setError(`Menge „${c.amount}“: z. B. „4 cl“ oder „0,2 l“`);
      parts.push({ article: c.article, ml });
    }
    await store.edit("recipe", recipe?.id ?? uuidv7(Date.now()), {
      name: name.trim(),
      kind,
      glasses: parsed.value,
      cardText: text.trim() || null,
      listNo: listNo.trim() || null,
      rubric: rubric || null,
      additives,
      allergens,
      components: parts,
      offCard: offCard || null,
    });
    onDone();
  }

  return (
    <li className="card recipe-form" data-testid="recipe-form">
      <label>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Gin Tonic" data-testid="recipe-name" />
      </label>
      <label>
        Art
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="recipe-kind">
          {RECIPE_KINDS.map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
        <span className="hint">Aperitif, Longdrink und Cocktail haben einen eigenen Teil; Saftschorle und Mocktail stehen bei den Alkoholfreien, eine Weinschorle bei den offenen Weinen.</span>
      </label>
      <label>
        Glasgrößen und Preise
        <input value={glasses} onChange={(e) => setGlasses(e.target.value)} placeholder="0,3 l 9,50" data-testid="recipe-glasses" />
      </label>
      <label>
        Kartennummer
        <input value={listNo} onChange={(e) => setListNo(e.target.value)} className="narrow" />
      </label>
      <label>
        Kartentext
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      {rubrics.length > 0 && (
        <label>
          Rubrik
          <select value={rubric} onChange={(e) => setRubric(e.target.value)}>
            <option value="">– nach der Art</option>
            {rubrics.map((r) => (
              <option key={r.id} value={r.id}>
                {r.title}
              </option>
            ))}
          </select>
        </label>
      )}
      <fieldset>
        <legend>Bestandteile (freiwillig)</legend>
        {components.map((c, i) => (
          <div key={i} className="row component">
            <select value={c.article} onChange={(e) => setComponents(components.map((x, j) => (j === i ? { ...x, article: e.target.value } : x)))}>
              <option value="">bitte wählen</option>
              {drinks.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <input className="narrow" value={c.amount} placeholder="4 cl" onChange={(e) => setComponents(components.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
            <button className="link" onClick={() => setComponents(components.filter((_, j) => j !== i))} aria-label="entfernen">
              ×
            </button>
          </div>
        ))}
        <button className="link" onClick={() => setComponents([...components, { article: "", amount: "" }])} data-testid="recipe-component">
          + Bestandteil
        </button>
        {missing.length > 0 && <p className="warn">Kein Bestand: {missing.join(", ")}. Das Getränk bleibt auf der Karte.</p>}
      </fieldset>
      <fieldset>
        <legend>Zusatzstoffe</legend>
        <div className="chips">
          {ADDITIVES.map((a) => (
            <button key={a} className={additives.includes(a) ? "active" : ""} onClick={() => setAdditives(toggle(additives, a))}>
              {a}
            </button>
          ))}
        </div>
        {proposed.additives.length > 0 && (
          <p className="hint">
            Aus den Bestandteilen: {proposed.additives.join(", ")}{" "}
            <button className="link inline" onClick={() => setAdditives([...additives, ...proposed.additives])}>
              übernehmen
            </button>
          </p>
        )}
      </fieldset>
      <fieldset>
        <legend>Allergene</legend>
        <div className="chips">
          {ALLERGENS.map((a) => (
            <button key={a} className={allergens.includes(a) ? "active" : ""} onClick={() => setAllergens(toggle(allergens, a))}>
              {a}
            </button>
          ))}
        </div>
        {proposed.allergens.length > 0 && (
          <p className="hint">
            Aus den Bestandteilen: {proposed.allergens.join(", ")}{" "}
            <button className="link inline" onClick={() => setAllergens([...allergens, ...proposed.allergens])}>
              übernehmen
            </button>
          </p>
        )}
      </fieldset>
      <label className="check">
        <input type="checkbox" checked={offCard} onChange={(e) => setOffCard(e.target.checked)} />
        nicht auf der Karte
      </label>
      {error && <p className="error">{error}</p>}
      <p className="row">
        <button className="primary" onClick={save} data-testid="recipe-save">
          Speichern
        </button>
        <button className="link" onClick={onDone}>
          abbrechen
        </button>
        {recipe && (
          <button className="link" onClick={() => store.edit("recipe", recipe.id, { archived: true }).then(onDone)}>
            löschen
          </button>
        )}
      </p>
    </li>
  );
}

/** Lines of one wine together: "Rosé 2023 (Flasche, 1/8 l, 1/4 l)" instead of three lines. */
function byWine(lines: PrintedLine[]): { key: string; name: string; offers: string[] }[] {
  const out = new Map<string, { key: string; name: string; offers: string[] }>();
  // The bottle first, then the glasses from small to large.
  const ml = (l: PrintedLine) => Number(l.key.split("|")[1]) || 0;
  for (const l of [...lines].sort((a, b) => ml(a) - ml(b))) {
    const [, base, offer] = /^(.*?)(?: \((.*)\))?$/.exec(l.name)!;
    const article = l.key.split("|")[0]!;
    const w = out.get(article) ?? { key: article, name: base!, offers: [] };
    w.offers.push(offer ?? "Flasche");
    out.set(article, w);
  }
  return [...out.values()];
}

/** Whether the printed card still says what is true, and if not, what changed since. */
function PrintNote({ status, own, web = false }: { status: { printedAt: number; diff: CardDiff } | null; own: boolean; web?: boolean }) {
  const what = own ? "Kellerliste" : "Karte";
  const testid = web ? "card-web-note" : "card-print-note";
  const drinks = useDrinks();
  // „Die gedruckte Karte“ or „Die Karte im Netz“.
  const which = web ? "Karte im Netz" : `gedruckte ${what}`;
  if (!status) return <p className="print-note" data-testid={testid}>Noch nicht gedruckt. Beim Drucken merkt sich die App, was auf der {what} steht.</p>;
  const date = new Date(status.printedAt).toLocaleDateString("de-DE");
  const added = byWine(status.diff.added);
  const gone = byWine(status.diff.gone);
  const { changed } = status.diff;
  const offers = (w: { offers: string[] }) => (w.offers.length === 1 && w.offers[0] === "Flasche" ? "" : ` (${w.offers.join(", ")})`);
  if (isUpToDate(status.diff)) return <p className="print-note ok" data-testid={testid}>Die {which} vom {date} ist aktuell.</p>;
  const value = (v: number | null) => (v === null ? "–" : own ? `${v} Fl.` : euro(v));
  const counts = [
    added.length ? `${added.length} ${added.length === 1 ? (drinks ? "Getränk" : "Wein") : drinks ? "Getränke" : "Weine"} neu` : "",
    gone.length ? `${gone.length} ${gone.length === 1 ? (drinks ? "Getränk" : "Wein") : drinks ? "Getränke" : "Weine"} entfallen` : "",
    changed.length ? `${changed.length} ${own ? (changed.length === 1 ? "Bestand" : "Bestände") : changed.length === 1 ? "Preis" : "Preise"} geändert` : "",
  ].filter(Boolean);
  return (
    <details className="print-note outdated" data-testid={testid}>
      <summary>
        Die {which} vom {date} ist veraltet: {counts.join(", ")}.
      </summary>
      <ul>
        {added.map((w) => (
          <li key={`a${w.key}`}>
            neu: {w.name}
            {offers(w)}
          </li>
        ))}
        {gone.map((w) => (
          <li key={`g${w.key}`}>
            entfallen: {w.name}
            {offers(w)}
          </li>
        ))}
        {changed.map((c) => (
          <li key={`c${c.line.key}`}>
            {c.line.name}: {value(c.was)} → {value(c.line.value)}
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Own styles for the Word file: change the styles in a Word file of the app, upload it, and every
 * new Word file uses them; „Standard-Formatvorlage wiederherstellen“ goes back to the app's styles.
 */
function WordStylesBox({ own }: { own: WordStyles | null }) {
  const { store } = useStore();
  const [error, setError] = useState<string | null>(null);
  async function upload(file: File | undefined) {
    if (!file) return;
    const { readWordStyles } = await import("../cardDocx.ts");
    const read = await readWordStyles(file, file.name);
    if ("error" in read) return setError(read.error);
    setError(null);
    await store.edit("card", "word", { ...read });
  }
  function reset() {
    setError(null);
    void store.edit("card", "word", { styles: null, theme: null, page: null, file: null, at: null });
  }
  return (
    <details className="word-styles" data-testid="word-styles">
      <summary>Formatvorlage für Word</summary>
      <p data-testid="word-state">
        Zurzeit: {own ? `eigene aus „${own.file}“ vom ${new Date(own.at).toLocaleDateString("de-DE")}` : "die eingebaute Formatvorlage der App"}
      </p>
      <p className="muted">
        Word-Datei erstellen, darin die Formatvorlagen ändern (etwa „Wein“, „Kartentext“, „Abschnitt“, „Preis“), speichern und hier hochladen. Ab dann hat jede neue Word-Datei diese Formatvorlagen, auch Seitengröße und Ränder.
      </p>
      <p className="row">
        <label className="button">
          Word-Datei hochladen …
          <input type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" hidden onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="word-upload" />
        </label>
      </p>
      {error && <p className="error">{error}</p>}
      {own && (
        <div className="word-reset">
          <p className="muted">
            Die eigene Formatvorlage ersetzt die eingebaute der App. Wer zu ihr zurück will, stellt sie hier wieder her; die eigene wird dabei gelöscht. Sie lässt sich jederzeit wieder hochladen.
          </p>
          <button onClick={reset} data-testid="word-reset">
            Standard-Formatvorlage wiederherstellen
          </button>
        </div>
      )}
    </details>
  );
}

/** Opens a page in a new tab (the preview of the card on the net). */
function openPage(html: string) {
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * The card on the net (F22, docs/15-getraenkekarte.md): the link with its QR code for the tables,
 * downloads and stickers, and how the page behaves. The link comes from the server once; it stays.
 */
/** Whether two sets of frozen translations say the same: the same languages, the same texts. */
function sameTexts(a: Partial<Record<CardLang, Record<string, string>>>, b: Partial<Record<CardLang, Record<string, string>>>): boolean {
  const norm = (t: typeof a) => JSON.stringify(CARD_LANGS.filter((l) => t[l]).map((l) => [l, Object.entries(t[l]!).sort(([x], [y]) => (x < y ? -1 : 1))]));
  return norm(a) === norm(b);
}

function WebCardBox({ catalog, parts, house, allergens, published, texts, textsChanged, onPublish }: { catalog: Catalog; parts: CardPart[]; house: string; allergens: "card" | "list"; published: boolean; texts: Partial<Record<CardLang, Record<string, string>>>; textsChanged: boolean; onPublish: () => void }) {
  const { store } = useStore();
  const fields = catalog.cards.get("web");
  const settings = webCardSettingsOf(fields);
  const [token, setToken] = useState<string | null | undefined>(undefined);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [address, setAddress] = useState(settings.address);
  const [imprint, setImprint] = useState(typeof fields?.imprint === "string" ? fields.imprint : "");
  const waiting = store.replica?.pending.some((op) => op.type === "entity" && op.change.type === "card" && op.change.id === "web") ?? false;

  const ask = (init?: RequestInit) =>
    store
      .request<{ token: string | null; url?: string | null }>("/api/card/link", init)
      .then((r) => {
        setError(null);
        setToken(r.token);
        setLinkUrl(r.url ?? null);
      })
      .catch((e: unknown) => setError(e instanceof Error && !/fetch|abort/i.test(e.message) ? e.message : "Ohne Verbindung zum Server lässt sich der Link nicht abrufen."));
  useEffect(() => {
    void ask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The server names the public address (the move to mzapps.cloud); an older one only the token.
  const url = token ? (linkUrl ?? `${window.location.origin}/k/${token}`) : null;
  const make = () => ask({ method: "POST", body: "{}" });
  const renew = () => {
    if (window.confirm("Einen neuen QR-Code erzeugen? Der bisherige Link funktioniert danach nicht mehr: Alle gedruckten Aufkleber und Tischaufsteller zeigen ins Leere und müssen ersetzt werden.")) void ask({ method: "POST", body: JSON.stringify({ renew: true }) });
  };
  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Link zur Karte", url);
    }
  };
  const file = `QR-Code ${house}`;
  const svg = () => url && save(new Blob([qrSvg(url)], { type: "image/svg+xml" }), `${file}.svg`);
  const png = async (px: number) => url && save(await qrPng(url, px), `${file} ${px}.png`);
  // Right in the tap, without waiting: otherwise Safari does not let the page print.
  const stickers = () => url && print(stickerSheetHtml(url, house, cardName(parts)));
  const langs = cardLangsOf(fields?.languages);
  const preview = (lang?: CardLang) => {
    const card = { publishedAt: Date.now(), house, parts, allergens, texts };
    openPage(cardWebHtml(card, { address: settings.address, imprint: settings.imprint }, webViewOf(card, lang)));
  };
  const switchLang = (lang: CardLang, on: boolean) => store.edit("card", "web", { languages: CARD_LANGS.filter((l) => (l === lang ? on : langs.includes(l))) });
  const saveImprint = () => {
    const v = imprint.trim();
    if (v && !imprintLink(v)) return setError("Der Link zum Impressum muss mit https:// beginnen.");
    setError(null);
    if (v !== (fields?.imprint ?? "")) void store.edit("card", "web", { imprint: v });
  };
  const [views, setViews] = useState<{ day: string; count: number }[] | null>(null);
  useEffect(() => {
    if (token)
      store
        .request<{ days: { day: string; count: number }[] }>("/api/card/views")
        .then((r) => setViews(r.days))
        .catch(() => setViews(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const qr = useMemo(() => (url ? qrSvg(url, "QR-Code der Karte") : ""), [url]);

  return (
    <details className="word-styles web-card" data-testid="web-card">
      <summary>Karte im Netz</summary>
      <p className="muted">
        Die Karte für das Handy der Gäste, über einen QR-Code auf dem Tisch. Öffentlich, ohne Anmeldung. Sie zeigt den Stand vom letzten „Veröffentlichen“; ausgetrunkene Getränke fallen von selbst weg, wenn das unten eingeschaltet ist.
      </p>
      <p data-testid="web-state">
        {!published ? "Noch nicht veröffentlicht." : settings.offline ? "Offline genommen: Der Link zeigt „zurzeit nicht verfügbar“." : `Veröffentlicht am ${new Date(webCardOf(fields)!.publishedAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}.`}
        {waiting && " Wartet auf Verbindung, dann ist sie live."}
      </p>
      <p className="row">
        <button className="primary" onClick={onPublish} data-testid="web-publish">
          {published ? "Neu veröffentlichen" : "Veröffentlichen"}
        </button>
        <button onClick={() => preview()} data-testid="web-preview">
          Vorschau
        </button>
        {langs.map((l) => (
          <button key={l} className="link" onClick={() => preview(l)} data-testid={`web-preview-${l}`}>
            {CARD_LANG_LABELS[l]}
          </button>
        ))}
      </p>
      {textsChanged && (
        <p className="print-note outdated" data-testid="web-texts-note">
          Sprachen oder Übersetzungen haben sich seit dem Veröffentlichen geändert. „Neu veröffentlichen“ bringt sie ins Netz.
        </p>
      )}
      {token === null && (
        <p className="row">
          <button onClick={make} data-testid="web-make">
            QR-Code erzeugen
          </button>
        </p>
      )}
      {url && (
        <div className="web-link">
          <div className="web-qr" data-testid="web-qr" dangerouslySetInnerHTML={{ __html: qr }} />
          <p className="web-url">
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid="web-url">
              {url.replace(/^https?:\/\//, "")}
            </a>
          </p>
          <p className="row wrap">
            <button onClick={copy}>{copied ? "Kopiert" : "Link kopieren"}</button>
            <button onClick={stickers} data-testid="web-stickers">
              Aufkleber drucken
            </button>
          </p>
          <p className="row wrap">
            <span className="muted">Herunterladen:</span>
            <button className="link" onClick={svg} data-testid="web-svg">
              SVG
            </button>
            {[512, 1024, 2048].map((px) => (
              <button key={px} className="link" onClick={() => png(px)} data-testid={`web-png-${px}`}>
                PNG {px}
              </button>
            ))}
          </p>
          {views && <ViewsChart days={views} />}
          <p className="muted small">Die Adresse im QR-Code bleibt für immer gleich, auch nach neuem Veröffentlichen. Die Testinstanz ist nicht für Aufkleber im echten Betrieb gedacht.</p>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      <div className="web-settings">
        <label className="check">
          <input type="checkbox" checked={settings.hideSoldOut} onChange={(e) => store.edit("card", "web", { hideSoldOut: e.target.checked })} data-testid="web-hide" />
          Ausgetrunkenes ausblenden
        </label>
        <label>
          Anschrift (im Fuß der Karte)
          <input value={address} placeholder="Hauptstraße 1, 79098 Freiburg" onChange={(e) => setAddress(e.target.value)} onBlur={() => address.trim() !== settings.address && store.edit("card", "web", { address: address.trim() })} data-testid="web-address" />
        </label>
        <label>
          Link zum Impressum
          <input value={imprint} placeholder="https://…/impressum" inputMode="url" onChange={(e) => setImprint(e.target.value)} onBlur={saveImprint} data-testid="web-imprint" />
        </label>
        <fieldset className="web-langs" data-testid="web-langs">
          <legend>Weitere Sprachen der Karte</legend>
          <p className="muted small">Gäste wählen oben rechts „DE · EN …“, sonst entscheidet ihr Browser. Überschriften, Allergene und die festen Wörter der Seite übersetzt die App; Kartentexte und eigene Überschriften unter „Übersetzungen“. Was fehlt, steht deutsch da. Gilt ab dem nächsten „Veröffentlichen“.</p>
          {CARD_LANGS.map((l) => (
            <label key={l} className="check">
              <input type="checkbox" checked={langs.includes(l)} onChange={(e) => switchLang(l, e.target.checked)} data-testid={`web-lang-${l}`} />
              {CARD_LANG_LABELS[l]}
            </label>
          ))}
        </fieldset>
      </div>
      {langs.length > 0 && <TranslationsBox parts={parts} langs={langs} phrases={cardPhrasesOf(catalog.cards.get(PHRASES_ID))} />}
      {url && (
        <div className="word-reset">
          <p className="muted">„Offline nehmen“ blendet die Karte aus, etwa in den Betriebsferien; die Aufkleber bleiben gültig. Ein neuer QR-Code ist nur bei Missbrauch nötig: Danach funktionieren alle bisherigen Aufkleber nicht mehr.</p>
          <button onClick={() => store.edit("card", "web", { offline: !settings.offline })} data-testid="web-offline">
            {settings.offline ? "Wieder online stellen" : "Karte offline nehmen"}
          </button>
          <button className="danger" onClick={renew} data-testid="web-renew">
            Neuen QR-Code erzeugen
          </button>
        </div>
      )}
    </details>
  );
}

/**
 * The house's own translations of the card on the net (F28): every text of the card once, by where it
 * comes from, with a field per text for the chosen language. A field shows the base vocabulary or the
 * country's name as „Vorgabe“; unchanged it is not saved, so the base stays looked up. Names stay as
 * they are unless one is written here; an empty field takes a translation away.
 */
function TranslationsBox({ parts, langs, phrases }: { parts: CardPart[]; langs: CardLang[]; phrases: CardPhrases }) {
  const { store } = useStore();
  const [chosen, setChosen] = useState<CardLang>(langs[0]!);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const lang = langs.includes(chosen) ? chosen : langs[0]!;
  const own = phrases[lang] ?? {};
  const all = cardPhrases(parts);
  const found = (text: string) => phraseOf(text, lang, own);
  const missing = all.filter((p) => p.origin !== "name" && !found(p.text));
  const shown = onlyMissing ? missing : all;
  const save = (text: string, value: string) => {
    const v = phraseKey(value);
    const base = found(text);
    if (v === (own[text] ?? "") || (base?.by === "base" && v === base.text)) return;
    void store.edit("card", PHRASES_ID, phraseChange(lang, text, v));
  };
  return (
    <details className="word-styles translations" data-testid="web-translations">
      <summary>
        Übersetzungen
        {missing.length > 0 && <span className="muted"> · {missing.length} fehlen</span>}
      </summary>
      {langs.length > 1 && (
        <div className="variants">
          {langs.map((l) => (
            <button key={l} className={l === lang ? "active" : ""} onClick={() => setChosen(l)} data-testid={`translations-${l}`}>
              {CARD_LANG_LABELS[l]}
            </button>
          ))}
        </div>
      )}
      <p className="muted small">
        {missing.length ? `${missing.length} von ${all.filter((p) => p.origin !== "name").length} Texten ohne ${CARD_LANG_LABELS[lang]}; sie stehen deutsch da.` : `Alle Texte haben ${CARD_LANG_LABELS[lang]}.`} Namen von Getränken bleiben, wie sie sind, außer Sie tragen eine Übersetzung ein; Weine, Erzeuger, Regionen und Rebsorten bleiben immer.
      </p>
      <label className="check">
        <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="translations-missing" />
        Nur fehlende zeigen
      </label>
      {(Object.keys(PHRASE_ORIGINS) as PhraseOrigin[]).map((origin) => {
        const items = shown.filter((p) => p.origin === origin);
        if (!items.length) return null;
        return (
          <div key={origin} className="phrase-group">
            <h4>{PHRASE_ORIGINS[origin]}</h4>
            {items.map(({ text }) => {
              const f = found(text);
              const value = f?.text ?? "";
              const long = origin === "text";
              const props = {
                defaultValue: value,
                placeholder: origin === "name" ? `bleibt „${text}“` : `${CARD_LANG_LABELS[lang]} eintragen`,
                onBlur: (e: { target: { value: string } }) => save(text, e.target.value),
                "data-testid": "translation",
                lang,
              };
              return (
                <label key={`${lang}|${text}|${value}`} className="phrase">
                  <span className="phrase-de">
                    {text}
                    {f?.by === "base" && <span className="muted small"> · Vorgabe</span>}
                  </span>
                  {long ? <textarea rows={3} {...props} /> : <input {...props} />}
                </label>
              );
            })}
          </div>
        );
      })}
    </details>
  );
}

/**
 * How often guests opened the card on the net (F22): today, 7 and 30 days, and a bar per day.
 * One number per day on the server, nothing about who looked.
 */
function ViewsChart({ days }: { days: { day: string; count: number }[] }) {
  const sum = (n: number) => days.slice(-n).reduce((a, d) => a + d.count, 0);
  const max = Math.max(1, ...days.map((d) => d.count));
  const label = (d: { day: string; count: number }) => `${new Date(`${d.day}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "numeric" })}: ${d.count} ${d.count === 1 ? "Aufruf" : "Aufrufe"}`;
  return (
    <div className="web-views" data-testid="web-views">
      <p className="web-views-title">Aufrufe durch Gäste</p>
      <div className="web-views-stats">
        <span>
          <b data-testid="web-views-today">{sum(1)}</b> heute
        </span>
        <span>
          <b>{sum(7)}</b> 7 Tage
        </span>
        <span>
          <b data-testid="web-views-30">{sum(30)}</b> 30 Tage
        </span>
      </div>
      <div className="web-views-bars" role="img" aria-label={`Aufrufe der letzten 30 Tage: ${days.map(label).join(", ")}`}>
        {days.map((d) => (
          <span key={d.day} title={label(d)}>
            <i style={{ height: `${d.count === 0 ? 0 : Math.max(8, (d.count / max) * 100)}%` }} />
          </span>
        ))}
      </div>
      <div className="web-views-axis muted">
        <span>{new Date(`${days[0]!.day}T12:00:00`).toLocaleDateString("de-DE", { day: "numeric", month: "numeric" })}</span>
        <span>heute</span>
      </div>
      <p className="muted small">Je Tag gezählt; wer sie aufgerufen hat, wird nicht gespeichert. Vorschau und Linkvorschauen in Messengern zählen nicht.</p>
    </div>
  );
}
